import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import { CostGroupEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerSetActKC2OtherCostCommentGql, uerSetActKC2WorkVolumeCommentGql } from '../../workOrderQueries';

interface IUseUpdateActPriceCommentProps {
    actCostGroup: CostGroupEnum;
    actId: number;
    priceId: number;
    onSuccess: (reloadRowsIds: number[]) => void;
    onCloseModal: () => void;
}

export const useUpdateActPriceComment = ({
    actCostGroup,
    actId,
    priceId,
    onSuccess,
    onCloseModal,
}: IUseUpdateActPriceCommentProps) => {
    const dispatch = useDispatch();

    const [resetData] = useResetMultipleStoreData(
        useMemo(
            () => ({
                requestIds: ['updateWorkOrderActOtherComment', 'updateWorkOrderActPriceComment'],
            }),
            [],
        ),
    );

    const onSuccessUpdate = useCallback(() => {
        onSuccess?.([priceId]);
        onCloseModal?.();
        resetData();
    }, [onCloseModal, onSuccess, priceId, resetData]);

    const [, updateCommentOtherStatus, , updateCommentOtherRequest] = useRequest({
        withEffect: false,
        query: uerSetActKC2OtherCostCommentGql,
        requestId: 'updateWorkOrderActOtherComment',
        dependenciesUpdater: onSuccessUpdate,
    });
    const [, updateCommentPriceStatus, , updateCommentPriceRequest] = useRequest({
        withEffect: false,
        query: uerSetActKC2WorkVolumeCommentGql,
        requestId: 'updateWorkOrderActPriceComment',
        dependenciesUpdater: onSuccessUpdate,
    });

    const submitHandler = useCallback(
        ({ comment }: { comment: string | undefined | null }) => {
            if (actCostGroup === CostGroupEnum.Others) {
                dispatch(
                    updateCommentOtherRequest({
                        actKC2Id: actId,
                        otherPriceId: priceId,
                        comment,
                    }),
                );
            } else if (actCostGroup === CostGroupEnum.Cmr) {
                dispatch(
                    updateCommentPriceRequest({
                        actKC2Id: actId,
                        priceId,
                        comment,
                    }),
                );
            }
        },
        [actCostGroup, actId, dispatch, priceId, updateCommentOtherRequest, updateCommentPriceRequest],
    );

    const isCommit =
        updateCommentOtherStatus === RequestStatus.FETCHING || updateCommentPriceStatus === RequestStatus.FETCHING;

    return {
        submitHandler,
        isCommit,
    };
};
